import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Paper
} from '@mui/material';
import {
  TrendingUp,
  Agriculture,
  LocalShipping,
  NotificationsActive,
  Warning,
  Assessment,
  Add,
  Refresh,
  Visibility,
  ChevronRight,
  HealthAndSafety,
  EventNote
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';

const DashboardEnhanced = () => {
  const { messages: socketMessages } = useSocket();
  const navigate = useNavigate();
  
  const { data: dashboardData, isLoading } = useQuery('dashboard', async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get('/api/dashboard/stats', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  });

  const { data: alertasData } = useQuery('alertas', async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get('/api/alertas', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  });

  const { data: movimientosData } = useQuery('movimientos', async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get('/api/movimientos', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  });

  const stats = {
    totalAnimales: dashboardData?.totalAnimales || 0,
    animalesSanos: dashboardData?.animalesSanos || 0,
    alertasActivas: dashboardData?.alertasActivas || 0,
    movimientosPendientes: dashboardData?.movimientosPendientes || 0,
    tratamientosEnCurso: dashboardData?.tratamientosEnCurso || 0,
    guiasEmitidas: dashboardData?.guiasEmitidas || 0
  };

  const newMessages = socketMessages.filter(m => m.type === 'ganadero');
  const movimientosRecientes = movimientosData?.movimientos?.slice(0, 5) || [];
  const alertasRecientes = alertasData?.alertas?.filter(a => a.estado === 'pendiente').slice(0, 4) || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'var(--navy-main)' }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, fontFamily: 'Montserrat' }}>
          BASERRI-ADITU
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, minHeight: '100vh', bgcolor: 'var(--bg-app)' }}>
      {/* 1. Header & Welcome Area */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)', mb: 0.5, letterSpacing: '-0.02em' }}>
            Panel de Control
          </Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)', fontWeight: 500 }}>
            Bienvenido al centro de gestión magistral de tu explotación.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip icon={<Refresh />} label="Actualizado hace un momento" variant="outlined" sx={{ borderRadius: '8px', fontWeight: 600 }} />
        </Box>
      </Box>

      {/* 2. Real-time Notifications Panel */}
      {newMessages.length > 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            mb: 4, 
            p: 2, 
            borderRadius: '16px', 
            background: 'linear-gradient(90deg, #eff6ff 0%, #ffffff 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#3b82f6', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>
              <NotificationsActive />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e3a8a' }}>
                {newMessages.length} Notificaciones del Asistente
              </Typography>
              <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                "{newMessages[newMessages.length-1].message}"
              </Typography>
            </Box>
          </Box>
          <Button 
            className="luxury-button"
            variant="contained" 
            onClick={() => navigate('/app/chat')}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            Atender Ahora
          </Button>
        </Paper>
      )}

      {/* 3. Top Metrics Strip (KPIs) */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card className="premium-card accent-green">
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'var(--emerald-light)', color: 'var(--forest-main)' }}><Agriculture /></Avatar>
                <TrendingUp sx={{ color: 'var(--emerald-main)' }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>{stats.totalAnimales}</Typography>
              <Typography variant="subtitle1" sx={{ color: 'var(--slate-light)', fontWeight: 700 }}>Animales Registrados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className="premium-card accent-green">
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#ecfdf5', color: '#059669' }}><HealthAndSafety /></Avatar>
                <Chip size="small" label="Salud Óptima" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>{stats.animalesSanos}</Typography>
              <Typography variant="subtitle1" sx={{ color: 'var(--slate-light)', fontWeight: 700 }}>Animales en Buen Estado</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className="premium-card" sx={{ borderColor: stats.alertasActivas > 0 ? 'var(--alert-gold)' : 'var(--border-color)' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#fffbeb', color: '#b45309' }}><Warning /></Avatar>
                {stats.alertasActivas > 0 && <Chip size="small" label="Requiere Atención" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />}
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>{stats.alertasActivas}</Typography>
              <Typography variant="subtitle1" sx={{ color: 'var(--slate-light)', fontWeight: 700 }}>Alertas Pendientes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 4. Main Activity Center (Two Columns) */}
      <Grid container spacing={4}>
        {/* Left Column: Quick Actions & Recent Movements */}
        <Grid item xs={12} lg={7}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: 'var(--navy-main)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping fontSize="small" /> Centro de Acción
          </Typography>
          <Card className="premium-card accent-navy" sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Button 
                    fullWidth 
                    className="luxury-button luxury-button-primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/app/movimientos')}
                  >
                    Movimiento
                  </Button>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Button 
                    fullWidth 
                    variant="outlined"
                    className="luxury-button"
                    startIcon={<Warning />}
                    sx={{ color: 'var(--navy-main)', borderColor: 'var(--border-color)' }}
                    onClick={() => navigate('/app/alertas')}
                  >
                    Nueva Alerta
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button 
                    fullWidth 
                    variant="outlined"
                    className="luxury-button"
                    startIcon={<Assessment />}
                    sx={{ color: 'var(--navy-main)', borderColor: 'var(--border-color)' }}
                    onClick={() => navigate('/app/censo')}
                  >
                    Ver Censo
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: 'var(--navy-main)' }}>
            Movimientos Recientes
          </Typography>
          <Card className="premium-card">
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {movimientosRecientes.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', color: 'var(--slate-light)' }}>
                  No hay movimientos registrados
                </Box>
              ) : movimientosRecientes.map((mov, index) => (
                <Box key={index}>
                  <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', '&:hover': { bgcolor: 'var(--slate-bg)' }, transition: '0.2s' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'var(--slate-bg)', color: 'var(--navy-main)' }}>
                        <LocalShipping />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{mov.tipo_movimiento}</Typography>
                        <Typography variant="body2" sx={{ color: 'var(--slate-light)' }}>{mov.destino} - {mov.fecha}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        label={mov.estado} 
                        size="small"
                        sx={{ 
                          fontWeight: 700, 
                          bgcolor: mov.estado === 'completado' ? 'var(--alert-mint)' : '#fef3c7',
                          color: mov.estado === 'completado' ? 'var(--emerald-main)' : '#b45309'
                        }} 
                      />
                      <IconButton size="small"><ChevronRight /></IconButton>
                    </Box>
                  </Box>
                  {index < movimientosRecientes.length - 1 && <Divider />}
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>

        {/* Right Column: Alerts & Status Summary */}
        <Grid item xs={12} lg={5}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: 'var(--navy-main)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive fontSize="small" /> Alertas del Sistema
          </Typography>
          <Card className="premium-card" sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              {alertasRecientes.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ color: 'var(--slate-light)', fontWeight: 600 }}>No hay alertas críticas</Typography>
                </Box>
              ) : (
                alertasRecientes.map((alerta, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid var(--border-color)', borderRadius: '12px', bgcolor: alerta.prioridad === 'alta' ? 'rgba(190, 18, 60, 0.03)' : 'transparent' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: alerta.prioridad === 'alta' ? 'var(--alert-crimson)' : 'var(--navy-main)' }}>
                        {alerta.titulo}
                      </Typography>
                      <Chip label={alerta.prioridad} size="small" sx={{ fontSize: '10px', height: '18px', bgcolor: alerta.prioridad === 'alta' ? 'var(--alert-crimson)' : 'var(--alert-gold)', color: 'white', fontWeight: 700 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'var(--slate-light)', fontWeight: 500 }}>{alerta.mensaje}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--slate-light)', mt: 1, display: 'block' }}>{alerta.fecha}</Typography>
                  </Box>
                ))
              )}
              <Button fullWidth sx={{ mt: 1, color: 'var(--emerald-main)', fontWeight: 700 }} endIcon={<ChevronRight />} onClick={() => navigate('/app/alertas')}>
                Ver todas las alertas
              </Button>
            </CardContent>
          </Card>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: 'var(--navy-main)' }}>
            Próximos Eventos
          </Typography>
          <Card className="premium-card accent-navy">
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'var(--navy-main)', color: 'white' }}><EventNote /></Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Vacunación Programada</Typography>
                  <Typography variant="body2" sx={{ color: 'var(--slate-light)' }}>Lote Bovino A - Próximo lunes</Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'var(--slate-bg)', color: 'var(--navy-main)' }}><Visibility /></Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Inspección Sanitaria</Typography>
                  <Typography variant="body2" sx={{ color: 'var(--slate-light)' }}>Pendiente de confirmar fecha</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardEnhanced;
